import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Password } from "primereact/password";
import { Button } from "primereact/button";
import { BrandLogo } from "../../components/logo";
import { completeAccess, inspectAccess } from "../../services/accountAccess.service";
import "./LoginPage.css";
import "./AccessPage.css";

function passwordIssue(value) {
  if (!value) return "Escribe tu nueva contraseña.";
  if (!value.trim()) return "La contraseña no puede contener solo espacios.";
  if (value.length < 12) {
    const remaining = 12 - value.length;
    return `Añade ${remaining} ${remaining === 1 ? "carácter" : "caracteres"} más para llegar al mínimo de 12.`;
  }
  if (value.length > 128) return "La contraseña no puede superar los 128 caracteres.";
  return "";
}

export default function AccessPage() {
  const [token] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get("token") || "");
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [touched, setTouched] = useState({ password: false, confirmation: false });
  const passwordInput = useRef(null);
  const confirmationInput = useRef(null);
  const errorBanner = useRef(null);
  const passwordError = passwordIssue(password);
  const confirmationError = !confirmation ? "Repite tu nueva contraseña para confirmarla."
    : password !== confirmation ? "Las contraseñas no coinciden." : "";
  const passwordInvalid = (submitted || touched.password) && !!passwordError;
  const confirmationInvalid = (submitted || touched.confirmation) && !!confirmationError;
  const matching = !!confirmation && password === confirmation && !passwordError;
  const validationError = submitted && (passwordError || confirmationError);
  const visibleError = error || validationError;

  useEffect(() => { if (error) errorBanner.current?.focus(); }, [error]);
  useEffect(() => {
    let active = true;
    if (!token) { setError("Abre el enlace que recibiste por correo."); setLoading(false); return; }
    inspectAccess(token).then(value => { if (active) setInfo(value); })
      .catch(err => { if (active) setError(err.message); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [token]);
  const submit = async event => {
    event.preventDefault();
    if (busy) return;
    setSubmitted(true);
    if (passwordError || confirmationError) {
      setError("");
      (passwordError ? passwordInput : confirmationInput).current?.focus();
      return;
    }
    setError(""); setBusy(true);
    try {
      await completeAccess(token, password);
      setPassword(""); setConfirmation(""); setSubmitted(false); setDone(true);
      window.history.replaceState(window.history.state, "", window.location.pathname);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };
  return <main className="login-wrapper">
    <section className="login-panel teamassess-form access-panel" aria-labelledby="access-title">
      <div className="login-header"><BrandLogo className="login-brand" />
        <h1 id="access-title" className="login-title">{done ? "Acceso actualizado" : info?.purpose === "ACTIVATE" ? "Activa tu cuenta" : "Nueva contraseña"}</h1>
      </div>
      {loading && <p role="status">Verificando enlace...</p>}
      {visibleError && !done && <div ref={errorBanner} id="access-error" role="alert" tabIndex={-1} className="access-alert">
        <i className="pi pi-exclamation-circle" aria-hidden="true" />
        <div><p className="access-alert-title">{error ? (info ? "No pudimos guardar la contraseña" : "No pudimos verificar el enlace") : "Revisa tu contraseña"}</p>
          <p>{visibleError}</p></div>
      </div>}
      {info && !done && <form onSubmit={submit} className="p-fluid" noValidate>
        <p className="access-account">Usuario: <strong>{info.username}</strong></p>
        <div className="field">
          <div className="access-label-row"><label htmlFor="access-password">Nueva contraseña</label>
            <span className="access-counter" aria-hidden="true">{password.length} caracteres</span></div>
          <Password inputId="access-password" value={password} onChange={e => setPassword(e.target.value)} required
            disabled={busy} feedback={false} toggleMask autoComplete="new-password" maxLength={128}
            inputRef={passwordInput} onBlur={() => setTouched(value => ({ ...value, password: true }))}
            invalid={passwordInvalid} aria-invalid={passwordInvalid}
            aria-describedby={`access-password-requirements${passwordInvalid ? " access-password-error" : ""}`} />
          <div id="access-password-requirements" className={`access-requirement${!passwordError ? " is-valid" : ""}`}>
            <i className={`pi ${!passwordError ? "pi-check-circle" : "pi-info-circle"}`} aria-hidden="true" />
            <span>Entre 12 y 128 caracteres. Puedes usar una frase.</span>
          </div>
          {passwordInvalid && <p id="access-password-error" className="access-field-error">{passwordError}</p>}
        </div>
        <div className="field"><label htmlFor="access-confirmation">Confirmar contraseña</label>
          <Password inputId="access-confirmation" value={confirmation} onChange={e => setConfirmation(e.target.value)} required
            disabled={busy} feedback={false} toggleMask autoComplete="new-password" maxLength={128}
            inputRef={confirmationInput} onBlur={() => setTouched(value => ({ ...value, confirmation: true }))}
            invalid={confirmationInvalid} aria-invalid={confirmationInvalid} aria-describedby="access-confirmation-feedback" />
          <div id="access-confirmation-feedback" className={`access-requirement${confirmationInvalid ? " is-invalid" : matching ? " is-valid" : ""}`} aria-live="polite">
            <i className={`pi ${confirmationInvalid ? "pi-exclamation-circle" : matching ? "pi-check-circle" : "pi-info-circle"}`} aria-hidden="true" />
            <span>{confirmationInvalid ? confirmationError : matching ? "Las contraseñas coinciden." : "Vuelve a escribir la misma contraseña."}</span>
          </div>
        </div>
        <Button type="submit" label="Guardar contraseña" icon="pi pi-check" loading={busy} disabled={busy} />
      </form>}
      {done && <p role="status">Tu contraseña quedó guardada. Inicia sesión con tu usuario y tu nueva contraseña.</p>}
      {!loading && <p className="mt-4"><Link to="/login">{done ? "Iniciar sesión" : "Volver al inicio de sesión"}</Link></p>}
    </section>
  </main>;
}
